import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, MessageCircle, BadgeCheck, Sparkles, Clock } from "lucide-react";
import { resolveImg, track } from "@/lib/api";
import { Stars } from "@/components/Stars";
import { FavouriteButton } from "@/components/FavouriteButton";
import { PremiumBadge } from "@/components/PremiumBadge";
import { Button } from "@/components/ui/button";

export function BusinessCard({ business, index = 0 }) {
  const b = business;
  const link = `${b.listing_type === "service" ? "/services" : ""}/${b.city_slug}/${b.category_slug}/${b.slug}`;
  const photo = resolveImg(b.photos?.[0]) || "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=800";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
      className="bg-white rounded-2xl border border-border/60 shadow-sm hover:shadow-lg transition-shadow duration-200 overflow-hidden flex flex-col"
      data-testid={`business-card-${b.slug}`}
    >
      <Link to={link} className="relative block h-44 overflow-hidden bg-secondary">
        <img src={photo} alt={b.name} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3 flex gap-1.5">
          {b.premium && <PremiumBadge />}
          {b.featured && !b.premium && (
            <span className="flex items-center gap-1 rounded-full bg-primary text-white px-2.5 py-1 text-xs font-bold shadow">
              <Sparkles className="w-3 h-3" /> Featured
            </span>
          )}
          {b.verified && (
            <span className="flex items-center gap-1 rounded-full bg-blue-600 text-white px-2.5 py-1 text-xs font-bold shadow">
              <BadgeCheck className="w-3 h-3" /> Verified
            </span>
          )}
        </div>
        <span className={`absolute bottom-3 right-3 flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold shadow ${b.open_now ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
          <Clock className="w-3 h-3" /> {b.open_now ? "Open" : "Closed"}
        </span>
        <FavouriteButton businessId={b.id} className="absolute top-3 right-3" />
        {typeof b.distance_km === "number" && (
          <span className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-xs font-bold shadow text-foreground">
            <MapPin className="w-3 h-3 text-primary" /> {b.distance_km} km
          </span>
        )}
      </Link>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <Link to={link} className="font-display font-bold text-lg leading-tight hover:text-primary transition-colors" data-testid={`business-name-${b.slug}`}>
            {b.name}
          </Link>
          <span className="text-sm font-semibold text-muted-foreground shrink-0">{b.price_range}</span>
        </div>

        <div className="flex items-center gap-2 mt-1.5">
          <Stars rating={b.rating} />
          <span className="text-sm font-bold">{b.rating || "New"}</span>
          <span className="text-xs text-muted-foreground">({b.reviews_count} reviews)</span>
        </div>

        <p className="text-xs font-semibold text-primary mt-2">{b.category_name}</p>
        <p className="flex items-start gap-1 text-sm text-muted-foreground mt-1 line-clamp-2">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {b.address}
        </p>

        <div className="flex gap-2 mt-4 pt-3 border-t border-border/60">
          <a href={`tel:${b.phone}`} onClick={() => track(b.id, "call")} className="flex-1">
            <Button variant="outline" className="w-full rounded-full h-10 gap-1.5 text-primary border-primary/30 hover:bg-primary/5" data-testid={`call-${b.slug}`}>
              <Phone className="w-4 h-4" /> Call
            </Button>
          </a>
          <a href={`https://wa.me/91${b.whatsapp}`} target="_blank" rel="noreferrer" onClick={() => track(b.id, "whatsapp")} className="flex-1">
            <Button className="w-full rounded-full h-10 gap-1.5 bg-green-600 hover:bg-green-700" data-testid={`whatsapp-${b.slug}`}>
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </Button>
          </a>
        </div>
        <Link to={link}>
          <Button variant="ghost" className="w-full rounded-full h-9 mt-2 text-sm font-semibold" data-testid={`view-details-${b.slug}`}>View Details</Button>
        </Link>
      </div>
    </motion.div>
  );
}
